import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Activity, Search, Loader2, Download, Filter, Calendar, User,
  LogIn, LogOut, Plus, Edit, Trash2, Check, RefreshCw, Clock,
  FileText, Shield, Building2, Users, BookOpen, IndianRupee
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_ICONS = {
  login: LogIn,
  logout: LogOut,
  create: Plus,
  update: Edit,
  delete: Trash2,
  approve: Check,
  reject: Trash2
};

const ACTION_COLORS = {
  login: 'green',
  logout: 'slate',
  create: 'blue',
  update: 'amber',
  delete: 'red',
  approve: 'emerald',
  reject: 'red'
};

const MODULE_ICONS = {
  centers: Building2,
  students: Users,
  courses: BookOpen,
  fees: IndianRupee,
  staff: Shield,
  default: FileText
};

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    user_type: '',
    action: '',
    module: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [page, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page, limit: 50 });
      
      if (filters.user_type) params.append('user_type', filters.user_type);
      if (filters.action) params.append('action', filters.action);
      if (filters.module) params.append('module', filters.module);
      
      const res = await axios.get(`${API}/admin/activity-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hr ago`;
    if (diff < 172800000) return 'Yesterday';
    
    return date.toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const exportLogs = () => {
    const csv = [
      ['Date', 'User', 'User Type', 'Action', 'Module', 'Description'].join(','),
      ...logs.map(log => [
        log.created_at,
        log.user_name,
        log.user_type,
        log.action,
        log.module,
        `"${log.description}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const clearFilters = () => {
    setFilters({ user_type: '', action: '', module: '' });
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Activity className="w-7 h-7 text-emerald-600" />
            Activity Logs
          </h2>
          <p className="text-slate-600 mt-1">Track all system activities and user actions</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-slate-100' : ''}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline" onClick={fetchLogs}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportLogs} className="bg-emerald-600 hover:bg-emerald-700">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">User Type</label>
              <select
                value={filters.user_type}
                onChange={(e) => setFilters({ ...filters, user_type: e.target.value })}
                className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md"
              >
                <option value="">All Types</option>
                <option value="super_admin">Super Admin</option>
                <option value="sub_admin">Sub Admin</option>
                <option value="center">Center</option>
                <option value="student">Student</option>
                <option value="employer">Employer</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Action</label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md"
              >
                <option value="">All Actions</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Module</label>
              <select
                value={filters.module}
                onChange={(e) => setFilters({ ...filters, module: e.target.value })}
                className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md"
              >
                <option value="">All Modules</option>
                <option value="centers">Centers</option>
                <option value="students">Students</option>
                <option value="courses">Courses</option>
                <option value="fees">Fees</option>
                <option value="staff">Staff</option>
                <option value="exams">Exams</option>
                <option value="jobs">Jobs</option>
                <option value="employers">Employers</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-lg font-medium">No activity logs found</p>
            <p className="text-sm">Activities will appear here once users start interacting with the system</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map(log => {
              const ActionIcon = ACTION_ICONS[log.action] || FileText;
              const ModuleIcon = MODULE_ICONS[log.module] || MODULE_ICONS.default;
              const color = ACTION_COLORS[log.action] || 'slate';
              
              return (
                <div key={log.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start gap-4">
                    {/* Action Icon */}
                    <div className={`p-2 rounded-lg bg-${color}-100`}>
                      <ActionIcon className={`w-5 h-5 text-${color}-600`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800">{log.user_name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          log.user_type === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                          log.user_type === 'sub_admin' ? 'bg-blue-100 text-blue-700' :
                          log.user_type === 'center' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {log.user_type?.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${color}-100 text-${color}-700`}>
                          {log.action?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-slate-600 mt-1">{log.description}</p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-2 text-xs text-slate-500 bg-slate-50 rounded p-2">
                          {JSON.stringify(log.details, null, 2)}
                        </div>
                      )}
                    </div>
                    
                    {/* Metadata */}
                    <div className="text-right text-sm">
                      <div className="flex items-center gap-1 text-slate-500">
                        <ModuleIcon className="w-4 h-4" />
                        <span className="capitalize">{log.module}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-400 mt-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(log.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <p className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
